import { useReducer, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import _ from 'lodash';
import { Box, Button } from '@material-ui/core';
import WarningRounded from '@material-ui/icons/WarningRounded';
import FormSection from '../../form/FormSection';
import NgpForm from '../../form/NgpForm';
import NgpTagWarningField from '../../form/NgpTagWarningField';
import { USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { useFindById, useTags } from '../../../services/FormQueryService';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import { useFormDeleteWrapper } from '../../../services/FormDeleteService';
import useBreadcrumb from '../../breadcrumb/UseBreadcrumb';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import { buildPageId, useEntityIdParam } from '../AdminLinkUtils';
import {
    AGENT_ALLOW_REBOOT,
    AGENT_CONTACT_FIELD,
    AGENT_DESCRIPTION_FIELD,
    AGENT_LOCATION_FIELD,
    AGENT_NAME_FIELD,
    AGENT_STATUS_DELETED,
    AGENT_STATUS_DISABLED,
    AGENT_STATUS_DOWN,
    AGENT_STATUS_FIELD,
    AGENT_STATUS_RUNNING,
    AGENT_TAGS_FIELD,
    AGENTS_FUNCTION_NAME,
    AGENT_NAME_MAX_LENGTH,
    AGENT_MAX_GLOBAL_TESTS,
} from './AgentConstants';
import { agentFilterId } from '../../filters';
import { AGENT_TYPE } from '../../matrix/MatrixConfigHelper';
import NgpTextField from '../../form/NgpTextField';
import NgpTextarea from '../../form/NgpTextarea';
import { NgpTagsField } from '../../form/NgpTagsField';
import NgpToggle from '../../form/NgpToggle';
import { AgentDetailWebProxy } from './AgentDetailWebProxy';
import { AgentDetailWiredAuth } from './AgentDetailWiredAuth';
import { buildWebProxy as buildDefaultWebProxy } from '../agent-settings/AgentSettingsHelper';
import { isHardware, isHardware3k, isWindows, hasWireless, isHardwareEdgeSensor } from './AgentHelper';
import {
    buildAgentWebProxy,
    buildBeaconSettings,
    buildWiFi,
    buildWired,
    prepareBeaconFields,
    prepareWebProxyFields,
    prepareWiFiFields,
    prepareWiredFields,
    useDeleteErrorHandler,
    useLatestAgentData,
} from './AgentDetailHelper';
import { useDataService } from '../../../services/ApiDataService';
import { AgentDetailBeacons } from './AgentDetailBeacons';
import { useDetailStyles } from './AgentStyles';
import { AgentDetailWiFi } from './AgentDetailWiFi';
import { AgentBTTUser } from './AgentBTTUser';
import AgentDetailActionButtons from './AgentDetailActionButtons';
import { AgentDetailAdapters } from './AgentDetailAdapters';
import { AgentDetailCryptoPolicy } from './AgentDetailCryptoPolicy';
import { AgentDetailVoIP } from './AgentDetailVoIP';
import AgentReadOnlyPrimary from './AgentReadOnlyPrimary';
import AgentReadOnlySecondary from './AgentReadOnlySecondary';
import { AgentDetailTests } from './AgentDetailTests';
import { ID_FIELD } from '../../form/FormConstants';
import Link from '../../links/Link';
import { SSID_TYPE_OWE_OPTION } from '../wifi-networks/WifiNetworkConstants';

const functionName = AGENTS_FUNCTION_NAME;
const goToUrl = '../';
const apiUrl = '/ipm/v1/admin/agents/';
const apiTagsUrl = '/ipm/v1/open/agents/tags';
const webProxyReadApiUrl = '/ipm/v1/admin/webproxies/default';
const globalAgentSettingsUrl = '/ipm/v1/admin/agents/globalAgentSettings';
const wifiNetworksUrl = '/ipm/v1/admin/ssidConfig';
const wiredNetworksUrl = '/ipm/v1/admin/wiredNetworks';
const defaultValues = {};
const healthButtonText = i18n._(t`Go to nPoint Health`);

const reportingLinkConfig = {
    target: 'agent-detail',
    mappedFilterParams: {
        [agentFilterId]: AGENT_TYPE,
    },
};

const rebootHelpTipText = i18n._(
    t`If enabled, the nPoint software will reboot the OS after updating, if required. Otherwise, the nPoint software will update but will not run until the OS is rebooted.`
);

function prepareSavedData(dataToSave, formData, latestAgentData, windows, hardware3k) {
    const putData = _.extend(_.cloneDeep(dataToSave), {
        _id: formData._id,
        status: latestAgentData[AGENT_STATUS_FIELD],
    });

    prepareWebProxyFields(putData, formData);
    prepareWiFiFields(putData, formData);
    prepareWiredFields(putData, formData);
    prepareBeaconFields(putData, windows, hardware3k);

    return putData;
}

function isDeleteDisabled(agent) {
    const agentStatus = _.get(agent, AGENT_STATUS_FIELD);
    return !(
        agentStatus === AGENT_STATUS_RUNNING ||
        agentStatus === AGENT_STATUS_DISABLED ||
        agentStatus === AGENT_STATUS_DOWN
    );
}

function isGlobal(agent) {
    return agent?.type === 'G';
}

function agentTestDataListReducer(state, action) {
    if (_.get(action, 'tests')) {
        return {
            ...state,
            tests: action.tests,
        };
    }
    if (_.get(action, 'agent') && _.get(state, 'agent._id') !== _.get(action, 'agent._id')) {
        return {
            ...state,
            agent: action.agent,
        };
    }
}

export default function AgentAdminDetail() {
    const classes = useDetailStyles();
    const recordId = useEntityIdParam();
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const { deleteWrapper, deleting } = useFormDeleteWrapper();
    const methods = useForm(
        _.extend(USE_FORM_DEFAULT_OPTIONS, {
            defaultValues,
        })
    );
    const { reset, setError, handleSubmit } = methods;
    const functionBreadCrumbs = [{ link: goToUrl, text: i18n._(t`nPoints`) }];
    const newBreadCrumbText = t`New nPoint`;
    const dataService = useDataService();
    const formSaveService = useFormSaveService();
    const isWindowsAgent = useRef(false);
    const isHardware3kAgent = useRef(false);
    const isHardwareAgent = useRef(false);
    const agentHasWireless = useRef(false);
    const isHardwareEdgeSensorAgent = useRef(false);
    const isGlobalAgent = useRef(false);
    const configuredWiFiNetworks = useRef([]);
    const configuredWiredNetworks = useRef([]);
    const { tags: agentTags } = useTags({ url: apiTagsUrl });
    const deleteDisabled = useRef(true);
    const { latestAgentData, refreshLatestAgentData } = useLatestAgentData(recordId);
    const [agentTestData, agentTestDataDispatch] = useReducer(agentTestDataListReducer, { tests: [], agent: {} });
    const deleteErrorHandler = useDeleteErrorHandler();
    const healthUrl = `../${recordId}/metrics`;
    const agentOverloaded =
        isGlobal(latestAgentData) && agentTestData?.tests && agentTestData?.tests.length > AGENT_MAX_GLOBAL_TESTS;

    /**
     * Post handling of data coming back from the server
     * @param response
     * @returns {*}
     */
    const postFindByIdHandler = async (response) => {
        isWindowsAgent.current = isWindows(response);
        isHardware3kAgent.current = isHardware3k(response);
        isHardwareAgent.current = isHardware(response);
        isGlobalAgent.current = isGlobal(response);
        agentHasWireless.current = hasWireless(response);
        isHardwareEdgeSensorAgent.current = isHardwareEdgeSensor(response);

        deleteDisabled.current = isDeleteDisabled(response);

        buildWiFi(response);
        buildWired(response);
        buildAgentWebProxy(response);
        agentTestDataDispatch({ agent: response });

        const defaultWebProxyResponse = await dataService.get(webProxyReadApiUrl);
        const globalAgentSettings = await dataService.get(globalAgentSettingsUrl);
        const wifiNetworks = await dataService.get(wifiNetworksUrl);
        const wiredNetworks = await dataService.get(wiredNetworksUrl);

        if (!isHardwareEdgeSensorAgent.current) {
            configuredWiFiNetworks.current =
                wifiNetworks.filter((item) => item.isDefault === true || item.authType !== SSID_TYPE_OWE_OPTION) || [];
        } else {
            configuredWiFiNetworks.current = wifiNetworks || [];
        }

        configuredWiredNetworks.current = wiredNetworks || [];

        return _.extend(
            {},
            response,
            buildDefaultWebProxy(defaultWebProxyResponse),
            buildBeaconSettings(globalAgentSettings, response)
        );
    };

    const { initialized, finding, formData } = useFindById({
        url: apiUrl,
        recordId,
        postFindByIdHandler,
        resetFunc: reset,
        defaultValues,
    });

    const breadcrumbs = useBreadcrumb(functionBreadCrumbs, recordId, formData, AGENT_NAME_FIELD, newBreadCrumbText, {
        reportingLinkConfig,
    });

    const handleFormSubmit = (dataToSave = {}) => {
        const putData = prepareSavedData(
            dataToSave,
            formData,
            latestAgentData,
            isWindowsAgent.current,
            isHardware3kAgent.current,
            isHardwareEdgeSensorAgent.current
        );

        // without this it can write to ssidconf/defaultWifi or ssidconf/'' which is not valid.
        if (putData.networkSSID === '') {
            for (let i = 0; i < configuredWiFiNetworks.current.length; i++) {
                if (_.get(configuredWiFiNetworks.current[i], 'isDefault') === true) {
                    _.set(putData, 'networkSSID', configuredWiFiNetworks.current[i]._id);
                    break;
                }
            }
        }

        return formSaveService.save({
            url: apiUrl,
            recordId: formData._id,
            dataToSave: _.extend(putData, { _id: formData._id }),
            setError,
        });
    };

    const handleConfirmedDelete = () => {
        return deleteWrapper({
            deleteFunction: () => dataService.put(`${apiUrl}/${recordId}`, { status: AGENT_STATUS_DELETED }),
            onDeleteGoTo: goToUrl,
            errorHandler: deleteErrorHandler,
        })();
    };

    const handleSubmitFunction = submitWrapper(handleFormSubmit, goToUrl);
    // const handleSBSSIDSubmitFunction = submitWrapper(handleBSSIDFormSubmit, goToUrl);

    return (
        <AdminPage
            breadcrumbs={breadcrumbs}
            pageId={buildPageId(functionName)}
            actions={
                <Actions>
                    <ConfirmDeleteButton
                        handleConfirmedDelete={handleConfirmedDelete}
                        plural={false}
                        disabled={deleteDisabled.current}
                    />
                    <AgentDetailActionButtons agent={latestAgentData} refreshLatestAgentData={refreshLatestAgentData} />
                    <Button onClick={handleSubmit(handleSubmitFunction)} variant="contained" color="primary">
                        <Trans>Save</Trans>
                    </Button>
                </Actions>
            }
        >
            <FormProvider {...methods}>
                <NgpForm
                    submitFunction={handleSubmitFunction}
                    initialized={initialized}
                    loading={finding || saving || deleting}
                >
                    <FormSection title={<Trans>nPoint Details</Trans>}>
                        {agentOverloaded && (
                            <div className={classes.globalAgentOverloadedWarning}>
                                <WarningRounded />
                                <Trans className={classes.globalAgentOverloadedWarningMessage}>
                                    This nPoint has been assigned to more than the maxiumum number of 25 tests - refer
                                    to Running Service Tests below to see which tests are Running.
                                </Trans>
                            </div>
                        )}

                        <AgentReadOnlyPrimary
                            agent={latestAgentData}
                            agentHasWireless={agentHasWireless}
                            isGlobalAgent={isGlobalAgent}
                        />

                        <NgpTextField
                            fieldName={AGENT_NAME_FIELD}
                            label={<Trans>nPoint Name</Trans>}
                            required
                            inputProps={{ size: 60, maxLength: AGENT_NAME_MAX_LENGTH }}
                        />

                        <NgpTextarea
                            fieldName={AGENT_DESCRIPTION_FIELD}
                            label={<Trans>nPoint Description</Trans>}
                            cols={60}
                            inputProps={{ maxLength: 225 }}
                        />

                        <NgpTextarea
                            fieldName={AGENT_LOCATION_FIELD}
                            label={<Trans>nPoint Location</Trans>}
                            cols={60}
                            inputProps={{ maxLength: 225 }}
                        />

                        <NgpTextarea
                            fieldName={AGENT_CONTACT_FIELD}
                            label={<Trans>nPoint Contact</Trans>}
                            cols={60}
                            inputProps={{ maxLength: 225 }}
                        />

                        <NgpTagsField
                            fieldName={AGENT_TAGS_FIELD}
                            defaultValue={formData[AGENT_TAGS_FIELD]}
                            label={<Trans>Tags</Trans>}
                            options={agentTags}
                        />

                        {latestAgentData && isGlobal(latestAgentData) && (
                            <NgpTagWarningField fieldName={AGENT_TAGS_FIELD} agentTestDataList={[agentTestData]} />
                        )}

                        {isWindowsAgent.current && (
                            <NgpToggle
                                fieldName={AGENT_ALLOW_REBOOT}
                                label={<Trans>Allow Reboot on Update</Trans>}
                                helpTipText={rebootHelpTipText}
                            />
                        )}
                    </FormSection>

                    {latestAgentData && (
                        <AgentDetailAdapters latestAgentData={latestAgentData} isHardware3kAgent={isHardware3kAgent} />
                    )}

                    <AgentDetailBeacons formData={formData} classes={classes} isHardware3kAgent={isHardware3kAgent} />

                    {isWindowsAgent.current && <AgentBTTUser />}
                    {latestAgentData && (
                        <AgentDetailWiFi
                            formData={formData}
                            classes={classes}
                            agentHasWireless={agentHasWireless}
                            configuredWiFiNetworks={configuredWiFiNetworks}
                            latestAgentData={latestAgentData}
                            isHardwareEdgeSensorAgent={isHardwareEdgeSensorAgent}
                        />
                    )}

                    {latestAgentData && (
                        <AgentDetailWiredAuth
                            formData={formData}
                            classes={classes}
                            isHardware3kAgent={isHardware3kAgent}
                            isHardwareEdgeSensorAgent={isHardwareEdgeSensorAgent}
                            configuredWiredNetworks={configuredWiredNetworks}
                            latestAgentData={latestAgentData}
                        />
                    )}

                    {isHardwareEdgeSensorAgent.current && latestAgentData && (
                        <AgentDetailCryptoPolicy
                            classes={classes}
                            latestAgentData={latestAgentData}
                            formData={formData}
                        />
                    )}

                    <AgentDetailWebProxy formData={formData} />

                    {latestAgentData && (
                        <AgentDetailVoIP latestAgentData={latestAgentData} agentHasWireless={agentHasWireless} />
                    )}

                    <AgentDetailTests
                        agentId={formData[ID_FIELD]}
                        agentType={formData.type}
                        agentTags={formData.tags}
                        agentTestDataDispatch={agentTestDataDispatch}
                    />

                    {latestAgentData && (
                        <AgentReadOnlySecondary
                            agent={latestAgentData}
                            isHardware3kAgent={isHardware3kAgent}
                            isHardwareEdgeSensorAgent={isHardwareEdgeSensorAgent}
                            isHardwareAgent={isHardwareAgent}
                            isGlobalAgent={isGlobalAgent}
                        />
                    )}

                    <Box>
                        <Link to={healthUrl}>
                            <Button>{healthButtonText}</Button>
                        </Link>
                    </Box>
                </NgpForm>
            </FormProvider>
        </AdminPage>
    );
}
